import React from 'react';
import axios from 'axios';
import { cloneDeep } from 'lodash-es';
import urlonStringify from './urlon';

import { hardodedJackpotResp } from '@/_temp/hardcodedJackpotResp';
import { useAppSelector, useAppDispatch } from '../store';
import { fetchSources } from '@/store/slices/dataSources';

const apiUrl = window.config.dataSourceApiUrl;

const cache: any = {
  loading: {},
  ref: {},
};
window._gds_cache = cache;

type GameInfoProps = {
  dsId?: string;
  filterKey?: string;
  filterValue?: string;
  limit?: number | null;
};

const digestMessage = async (message: string) => {
  const data = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
  return hashHex;
};

const genericDataSource = ({ dsId, filterKey, filterValue, limit }: GameInfoProps) => {
  const dispatch = useAppDispatch();
  const dataSource = useAppSelector((state) =>
    dsId && state.dataSources.items[dsId] ? state.dataSources.items[dsId] : null,
  );

  const authenticationToken = useAppSelector((state) => state.authentication.access_token);

  const [state, setState] = React.useState<{ data: any; loading: boolean; loaded: boolean; digestKey: string }>({
    data: {
      id: null,
      element_type: null,
      meta: null,
      data: null,
    },
    loading: true,
    loaded: false,
    digestKey: '',
  });

  React.useEffect(() => {
    if (dsId)
      digestMessage(JSON.stringify({ dsId, filterKey, filterValue })).then((resp) => {
        setState((v) => ({
          ...v,
          digestKey: resp,
        }));
      });
  }, [dsId, filterKey, filterValue]);

  //console.log('digestKey', state.digestKey);

  const processResponse = (
    stillMounted: boolean,
    resp: any,
    dsId: string,
    filterKey: string | undefined,
    filterValue: string | undefined,
    digestKey: string,
  ) => {
    let data = null;
    if (resp?.data?.data) {
      data = resp.data.data;
    } else if (resp?.data?.[dsId]) {
      data = resp.data[dsId];
    }

    const final = {
      id: dsId,
      data: data,
      element_type: resp?.data?.element_type,
      meta: resp?.data?.meta,
      filterKey,
      filterValue,
    };

    if (cache.ref[digestKey]) cache[digestKey] = final;

    if (stillMounted) {
      setState({
        data: final,
        loading: false,
        loaded: true,
        digestKey,
      });
    }

    return { [digestKey]: final };
  };

  React.useEffect(() => {
    let stillMounted = true;

    const digestKey = state.digestKey;

    if (authenticationToken && dsId && digestKey) {
      if (cache.ref[digestKey] == null) cache.ref[digestKey] = 0;
      cache.ref[digestKey] += 1;

      if (cache[digestKey]) {
        const data = cloneDeep(cache[digestKey]);
        setState({
          data: data,
          loading: false,
          loaded: true,
          digestKey,
        });
        return;
      } else if (cache.loading[digestKey]) {
        cache.loading[digestKey].promise
          .then((data: any) => {
            if (!stillMounted) return;
            if (!data[digestKey]) return;

            setState({
              data: data[digestKey],
              loading: false,
              loaded: true,
              digestKey,
            });
          })
          .catch((e: Error) => {
            console.log(e);
            setState((v) => ({ ...v, loading: false }));
          });
        return;
      } else {
        if (dataSource?.data) {
          cache.loading[digestKey] = {};
          cache.loading[digestKey].promise = new Promise((resolve) => {
            let data = dataSource.data;
            if (filterKey && filterValue) {
              data = dataSource.data.filter((d: any) => d?.[filterKey] === filterValue);
            }

            if (limit != null) {
              data = data.slice(0, limit);
            }

            resolve(
              processResponse(
                stillMounted,
                {
                  data: {
                    ...dataSource,
                    data: data,
                  },
                },
                dsId,
                filterKey,
                filterValue,
                digestKey,
              ),
            );
          });
          return;
        }

        dispatch(fetchSources({ ids: [dsId] }));
      }
    }

    return () => {
      stillMounted = false;
    };
  }, [dsId, state.digestKey, filterKey, filterValue, limit, authenticationToken, dataSource, dispatch]);

  return state;
};

export default genericDataSource;
